import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GraduationCap, CheckCircle, AlertCircle, Clock, Download, TrendingUp, Mail } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import API from '@/config/api';

const URGENCY_STYLES = {
  ok: { badge: 'bg-green-100 text-green-700', bar: 'bg-green-500', label: 'Complete' },
  in_progress: { badge: 'bg-blue-100 text-blue-700', bar: 'bg-blue-500', label: 'In Progress' },
  warning: { badge: 'bg-amber-100 text-amber-700', bar: 'bg-amber-500', label: 'Behind' },
  critical: { badge: 'bg-orange-100 text-orange-700', bar: 'bg-orange-500', label: 'Critical' },
  overdue: { badge: 'bg-red-100 text-red-700', bar: 'bg-red-500', label: 'Overdue' },
};

export default function ContinuingEducationPage() {
  const { getAuthHeaders } = useAuth();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState({ summary: [], annual_requirement: 15, staff_complete: 0, staff_at_risk: 0, total_staff: 0, days_remaining_in_year: 0 });

  useEffect(() => { fetchSummary(); /* eslint-disable-line */ }, [year]);

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/continuing-education/summary?year=${year}`, getAuthHeaders());
      setData(res.data);
    } catch {
      toast.error('Failed to load CE summary');
    }
  };

  const downloadStaffPdf = async (s) => {
    try {
      const res = await axios.get(`${API}/staff/${s.staff_id}/ce-hours/pdf?year=${year}`, {
        ...getAuthHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CE_Log_${(s.staff_name || 'staff').replace(/\s+/g, '_')}_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const emailStaffLog = async (s) => {
    const note = window.prompt(
      `Email the ${year} CE log to ${s.staff_name}?\n\nOptional message (leave blank to use default):`,
      s.is_complete ? '' : `You currently have ${s.total_hours} of 15 required CE hours. Please catch up before year end.`
    );
    if (note === null) return;
    try {
      const res = await axios.post(
        `${API}/staff/${s.staff_id}/ce-hours/email`,
        { year, message: note || '' },
        getAuthHeaders()
      );
      toast.success(`${res.data.delivered ? 'Email sent' : 'Email queued'} to ${res.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    }
  };

  const compliantPct = data.total_staff > 0 ? Math.round((data.staff_complete / data.total_staff) * 100) : 0;

  return (
    <DashboardLayout title="Continuing Education">
      <div className="space-y-6" data-testid="ce-overview-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold font-heading">Continuing Education — {year}</h1>
            <p className="text-sm text-muted-foreground">DCF 251.05(3) — 15 hours/year required per staff member</p>
          </div>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="ce-overview-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Card className="border-l-4 border-l-indigo-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Staff</p>
                  <p className="text-2xl font-bold">{data.total_staff}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Compliant</p>
                  <p className="text-2xl font-bold">{data.staff_complete} <span className="text-sm text-muted-foreground">({compliantPct}%)</span></p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">At Risk</p>
                  <p className="text-2xl font-bold">{data.staff_at_risk}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Days Left in Year</p>
                  <p className="text-2xl font-bold">{data.days_remaining_in_year}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Staff list */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
              Staff Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.summary.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No active staff found.</p>
            ) : (
              <div className="space-y-2">
                {data.summary.map(s => {
                  const style = URGENCY_STYLES[s.urgency] || URGENCY_STYLES.in_progress;
                  const label = s.is_complete ? URGENCY_STYLES.ok.label : style.label;
                  const badgeClass = s.is_complete ? URGENCY_STYLES.ok.badge : style.badge;
                  const barClass = s.is_complete ? URGENCY_STYLES.ok.bar : style.bar;
                  return (
                    <div key={s.staff_id} className="p-3 border rounded-lg" data-testid={`ce-row-${s.staff_id}`}>
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">{s.staff_name}</span>
                            {s.position && <Badge variant="outline" className="text-[10px]">{s.position}</Badge>}
                            <Badge className={cn('text-[10px]', badgeClass)}>{label}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {s.total_hours.toFixed(1)} / {data.annual_requirement} hours
                            {!s.is_complete && <span className="ml-2">• {s.remaining_hours.toFixed(1)} hrs remaining</span>}
                            {s.last_entry_date && <span className="ml-2">• Last entry {new Date(s.last_entry_date + 'T12:00:00').toLocaleDateString()}</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-sm font-bold">{s.percent_complete}%</span>
                          <Button size="sm" variant="ghost" onClick={() => downloadStaffPdf(s)} title="Download PDF" data-testid={`ce-pdf-${s.staff_id}`}>
                            <Download className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => emailStaffLog(s)} title="Email log to staff" data-testid={`ce-email-${s.staff_id}`}>
                            <Mail className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={cn('h-full transition-all', barClass)}
                          style={{ width: `${s.percent_complete}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
